import React, { useEffect, useState } from "react";
import axios from "../axios";

/**
 * 詳細モーダル（SightingModal）の所定(261006追加)。
 *
 *   帯       … その車のその日の所定と状態（タップで便の一覧）
 *   カードの札 … 所定の便の目撃に「◆ 所定 土4」
 *   便の一覧  … 所定の乗番の便を時刻順に並べ、この車の目撃と突き合わせる。
 *              運用解析ページへの入口はここに置く
 *
 * 所定は「その日の朝の所定」（バックエンドの RegularSnapshots）。
 * **マイページの印のオン／オフは見ない。**あれは表の印だけの設定。
 * 出す範囲は運用解析と同じ（呼ぶ側が canUseAnalysis で絞る）。
 */

const SKY = "#0ea5e9";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

const PILL = {
  regular: { label: "所定通り", bg: "#e0f2fe", color: "#0369a1" },
  substituted: { label: "代走あり", bg: "#fef3c7", color: "#b45309" },
  unseen: { label: "目撃なし", bg: "#f1f5f9", color: "#475569" },
  multiple: { label: null, bg: SKY, color: "#fff" },
};

const QUIET = {
  none: "所定なし",
  insufficient: "所定情報不足",
  not_analyzed: "運用解析外営業所",
  error: "所定を読み込めませんでした",
};

/**
 * その車のその日の所定。モーダルが開いている間だけ取る。
 *
 * @param reloadKey 目撃が変わったら取り直す（投稿・編集・削除のあと）
 * @returns null（読み込み中）か、API の応答（失敗なら { state: "error" }）
 */
export function useRegularDay({ enabled, depot, busNumber, date, reloadKey }) {
  const [day, setDay] = useState(null);

  useEffect(() => {
    if (!enabled || !depot || !busNumber || !date) {
      setDay(null);
      return;
    }

    let cancelled = false;
    const params = new URLSearchParams({ depot, bus_number: busNumber, date });

    axios
      .get(`/analysis/regular-day?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setDay(res.data);
      })
      .catch(() => {
        if (!cancelled) setDay({ state: "error", duties: [], marks: {} });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, depot, busNumber, date, reloadKey]);

  return day;
}

const dia = (size = 9, lift = 2) => (
  <span style={{ color: SKY, fontSize: `${size}px`, verticalAlign: `${lift}px`, marginRight: "4px" }}>◆</span>
);

function Pill({ state, count }) {
  const p = PILL[state];
  if (!p) return null;
  return (
    <span style={{
      fontSize: "11px", fontWeight: 700, borderRadius: "999px", padding: "2px 9px",
      whiteSpace: "nowrap", background: p.bg, color: p.color, flexShrink: 0,
    }}>
      {p.label ?? `所定乗番${count}本`}
    </span>
  );
}

function windowText(duty) {
  return `直近${duty.window_days}日中${duty.days}日`;
}

function quietSub(day) {
  const dt = DAY_TYPE_LABEL[day.day_type] || "";
  switch (day.state) {
    case "none":
      return `${dt}・直近${day.window_days}日では決まった乗番がありません`;
    case "insufficient":
      return day.window == null
        ? "その日の所定の記録がありません"
        : `${dt}・観測 ${day.window} / ${day.window_days} 日`;
    case "not_analyzed":
      return "この営業所は運用を解析していません";
    default:
      return "タップで運用を見る";
  }
}

/** 帯（モーダルのヘッダーのすぐ下） */
export function RegularBand({ day, onOpen }) {
  const loud = day && day.duties?.length > 0;
  const chev = <span style={{ color: loud ? "#7dd3fc" : "#cbd5e1", fontSize: "18px", fontWeight: 700, lineHeight: 1 }}>›</span>;

  let body;
  if (!day) {
    body = <div style={{ fontSize: "12px", color: "#94a3b8" }}>所定を読み込み中…</div>;
  } else if (loud) {
    const dt = DAY_TYPE_LABEL[day.day_type] || "";
    body = (
      <>
        <div style={{ minWidth: 0, flex: 1, lineHeight: 1.35 }}>
          <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0c4a6e" }}>
            {dia()}所定 {day.duties.map((d) => d.duty).join(" ・ ")}
          </div>
          <div style={{ fontSize: "11px", color: "#64748b" }}>
            {dt}・直近{day.window_days}日中{day.duties.map((d) => `${d.days}日`).join(" / ")}
          </div>
        </div>
        <Pill state={day.state} count={day.duties.length} />
      </>
    );
  } else {
    body = (
      <div style={{ minWidth: 0, flex: 1, lineHeight: 1.35 }}>
        <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#475569" }}>{QUIET[day.state] || QUIET.error}</div>
        <div style={{ fontSize: "11px", color: "#64748b" }}>{quietSub(day)}</div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!day}
      style={{
        display: "flex", alignItems: "center", gap: "10px", width: "100%",
        padding: "9px 14px 9px 16px", border: "none", textAlign: "left",
        background: loud ? "#f0f9ff" : "#f8fafc",
        borderBottom: `1px solid ${loud ? "#e0f2fe" : "#eef2f6"}`,
        cursor: day ? "pointer" : "default", fontFamily: "inherit",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {body}
      {day && chev}
    </button>
  );
}

/** カードの札（路線の横） */
export function RegularTag({ duty }) {
  if (!duty) return null;
  return (
    <span style={{
      display: "inline-block", marginLeft: "6px", marginBottom: "8px", verticalAlign: "top",
      fontSize: "10.5px", fontWeight: 700, color: "#0369a1", background: "#e0f2fe",
      borderRadius: "999px", padding: "3px 8px 3px 6px", whiteSpace: "nowrap",
    }}>
      {dia(8, 1)}所定 {duty}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${y.slice(-2)}/${m}/${d}`;
}

function routeNoText(no) {
  return (no || "").replace(/^\[ループ\]/, "ループ ");
}

function DutySection({ duty, showState }) {
  const total = duty.trips.length;
  const pct = total > 0 ? Math.round((100 * duty.sighted) / total) : 0;

  return (
    <div>
      <div style={{ padding: "10px 16px 8px", display: "grid", gap: "4px", borderBottom: "1px solid #e0f2fe" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "15px", fontWeight: 800, color: "#0c4a6e" }}>{dia(10, 2)}所定 {duty.duty}</span>
          <span style={{ fontSize: "11.5px", color: "#475569" }}>{windowText(duty)}</span>
          {showState && <Pill state={duty.state} />}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", fontSize: "11.5px", color: "#475569" }}>
          <span>目撃</span>
          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#0369a1" }}>
            {duty.sighted} / {total} 便
          </span>
        </div>
        <div style={{ height: 4, borderRadius: 99, background: "#e2e8f0", overflow: "hidden" }}>
          <span style={{ display: "block", height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #7dd3fc, #0284c7)" }} />
        </div>
      </div>
      <div style={{ padding: "4px 0" }}>
        {duty.trips.map((t, i) => {
          const seen = t.post_id != null;
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "44px 1fr auto", gap: "10px", alignItems: "center",
              padding: "7px 16px", borderBottom: i === duty.trips.length - 1 ? "none" : "1px solid #f1f5f9",
              fontSize: "13px",
            }}>
              <span style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", color: seen ? "#0f172a" : "#94a3b8" }}>
                {t.departure}
              </span>
              <span style={{ minWidth: 0 }}>
                <b style={{
                  display: "block", fontWeight: 600, color: seen ? "#0f172a" : "#94a3b8",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {t.start_full_name || t.start}
                </b>
                <small style={{ fontSize: "10.5px", color: "#94a3b8" }}>
                  {t.route_full_name || t.route}{t.route_no ? ` ・ ${routeNoText(t.route_no)}` : ""}
                </small>
              </span>
              {seen ? (
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#0369a1", whiteSpace: "nowrap" }}>◆ 目撃あり</span>
              ) : (
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap" }}>・ まだ</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** 帯から開く追加モーダル（便の一覧と、運用解析ページへの入口） */
export function RegularDutyModal({ isOpen, onClose, day, depot, busNumber, date, onGoAnalysis }) {
  if (!isOpen || !day) return null;

  const dt = DAY_TYPE_LABEL[day.day_type] || "";
  const hasDuties = day.duties?.length > 0;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(2,12,20,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px", boxSizing: "border-box",
      }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div
        role="dialog"
        aria-label="所定の便"
        style={{
          background: "#fff", borderRadius: "22px", width: "100%", maxWidth: "440px",
          maxHeight: "85dvh", display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: "14px 16px 10px", flexShrink: 0,
          background: "linear-gradient(170deg, #f0f9ff 0%, #e0f2fe 100%)",
          borderBottom: "1px solid #bae6fd",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px",
        }}>
          <div style={{ minWidth: 0, lineHeight: 1.4 }}>
            <div style={{ fontSize: "17px", fontWeight: 800, color: "#0c4a6e" }}>
              {hasDuties ? <>{dia(11, 3)}所定の便</> : QUIET[day.state] || QUIET.error}
            </div>
            <div style={{ fontSize: "11.5px", color: "#475569" }}>
              {depot}{busNumber} ・ {formatDate(date)}{dt ? `（${dt}）` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            style={{
              background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "50%",
              width: 34, height: 34, flexShrink: 0, cursor: "pointer", color: "#be123c",
              fontSize: "15px", fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {hasDuties ? (
            day.duties.map((d, i) => (
              <DutySection key={`${d.duty}-${i}`} duty={d} showState={day.duties.length > 1} />
            ))
          ) : (
            <div style={{ padding: "18px 16px", fontSize: "13px", color: "#475569", lineHeight: 1.7 }}>
              {quietSub(day)}
            </div>
          )}
        </div>

        <div style={{
          padding: "10px 16px 14px", flexShrink: 0,
          borderTop: "1px solid #e0f2fe", background: "#fafcff", display: "grid", gap: "6px",
        }}>
          <button
            type="button"
            onClick={onGoAnalysis}
            style={{
              background: "none", border: "none", padding: 0, textAlign: "left",
              color: "#6d28d9", cursor: "pointer", fontSize: "12.5px", fontWeight: 600,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            この車のこの日の運用を見る →
          </button>
          {hasDuties && (
            <small style={{ fontSize: "10.5px", color: "#94a3b8" }}>
              その日の朝の所定です。いまの所定とは違うことがあります。「まだ」はこの車の目撃が無いだけで、ほかの車が走ったかは見ていません。
            </small>
          )}
        </div>
      </div>
    </div>
  );
}
